/** @jsxImportSource @emotion/react */

import { Fragment } from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { AOSRJsonType, SecondaryActJsonType } from '../../Types/dataJSON'
import {
  frontPageHeadingFontSize,
  frontPageTitleFontSize,
  getRelativeSizePt,
} from '../molecules/basis'
import {
  IActTemplate,
  IFrontPageSection,
  IHeaderActSection,
  IInlineActSection,
  IPageSeparatorActSection,
  ISelectActSection,
  ISubHeaderActSection,
  ITableActSection,
  ITextActSection,
  ITextTemplateActSection,
  RowDataType,
} from '../template.type'

import { Inline } from './Inline'
import { TemplateText } from './TemplateText'

type FrontPageDivType = {
  orientation: string
}

const FrontPageDiv = styled.div<FrontPageDivType>`
  font-family: 'PT-Astra-Serif', serif;
  font-weight: bold;
  font-style: normal;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  height: ${props =>
    props.orientation === 'portrait'
      ? `${getRelativeSizePt(854)}`
      : `${getRelativeSizePt(605)}`};

  align-items: center;
  justify-content: center;
  border-top: 1px solid #ced2d6;
  border-bottom: 1px solid #ced2d6;
`
const Body = styled.div`
  font-family: 'PT-Astra-Serif', serif;
  font-weight: bold;
  font-style: normal;
  text-decoration: none;
  font-size: ${frontPageTitleFontSize};
  margin: ${getRelativeSizePt(30)};
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const Header = styled.div`
  margin: ${getRelativeSizePt(30)};
  width: 80%;
`

const Item = styled.div`
  font-family: 'PT-Astra-Serif', serif;
  font-weight: bold;
  font-style: normal;
  text-decoration: none;
  font-size: ${frontPageHeadingFontSize};

  width: fit-content;

  word-wrap: break-word;
  text-align: center;
  padding-top: ${getRelativeSizePt(4)};
`

const Title = styled.div`
  font-family: 'PT-Astra-Serif', serif;
  font-weight: bold;
  font-style: normal;
  text-decoration: none;
  font-size: ${frontPageTitleFontSize};
  margin: ${getRelativeSizePt(10)};
  word-wrap: break-word;
`

const Footer = styled.div`
  font-family: 'PT-Astra-Serif', serif;
  font-weight: bold;
  font-style: normal;
  text-decoration: none;
  font-size: ${frontPageTitleFontSize};
  margin: ${getRelativeSizePt(50)};
  width: 90%;
  display: flex;
  align-items: center;
  flex-direction: column;
  word-wrap: break-word;
`

const BodyItem = styled.div`
  font-family: 'PT-Astra-Serif', serif;
  font-weight: bold;
  font-style: normal;
  text-decoration: none;
  font-size: ${frontPageHeadingFontSize};
  max-width: ${getRelativeSizePt(605)};
  width: 80%;
  text-align: center;
  word-wrap: break-word;
  padding: ${getRelativeSizePt(4)};
`

type FrontPageType = {
  section: IFrontPageSection
  actItems: AOSRJsonType | SecondaryActJsonType
  orientation?: string
  onChange: (e: string, key: string, id?: string) => void
  onToggle: (
    item: (
      | IFrontPageSection
      | IInlineActSection
      | ITextTemplateActSection
      | ISelectActSection
      | ITextActSection
    ) & {
      value: string
    },
  ) => void
  getValue: ({
    value,
    sectionTemplate,
    template,
  }: {
    value: string | RowDataType[][]
    sectionTemplate:
      | IHeaderActSection
      | IInlineActSection
      | ITextTemplateActSection
      | ITableActSection
      | IFrontPageSection
      | ISelectActSection
      | IPageSeparatorActSection
      | ITextActSection
      | ISubHeaderActSection
    template?: IActTemplate
  }) => string | Array<Array<RowDataType>>
  selectedField?: any
}

export const FrontPage = ({
  section,
  actItems,
  orientation = 'portrait',
  selectedField,
  onChange,
  onToggle,
  getValue,
}: FrontPageType) => {
  const getSection = (
    sectionTemplate:
      | IHeaderActSection
      | IInlineActSection
      | ITextTemplateActSection
      | ITextActSection,
  ) => {
    switch (sectionTemplate.type) {
      case 'template_text': {
        return (
          <TemplateText
            key={(sectionTemplate as ITextTemplateActSection).text}
            actItems={actItems}
            fontSize={getRelativeSizePt(16)}
            section={sectionTemplate as ITextTemplateActSection}
            onChange={onChange}
            onToggle={onToggle}
          />
        )
      }

      case 'inline': {
        return (
          <Inline
            key={(sectionTemplate as IInlineActSection).items
              .map(item => item.key)
              .join()}
            actItems={actItems}
            getValue={getValue}
            paragraphFontSize={getRelativeSizePt(18)}
            section={sectionTemplate as IInlineActSection}
            selectedField={selectedField}
            subDescriptionFontSize={getRelativeSizePt(14)}
            onToggle={onToggle}
          />
        )
      }

      case 'text':
      default:
        return (
          <div
            key={sectionTemplate.key}
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
            `}
          >
            <Item>{sectionTemplate.title}</Item>
            <Item>{actItems[sectionTemplate.key]?.value}</Item>
          </div>
        )
    }
  }

  return (
    <FrontPageDiv orientation={orientation}>
      {section.header && <Header>{section.header.map(getSection)}</Header>}

      {section.body && (
        <Body>
          <Title>{section.title}</Title>
          {section.body.map(item => (
            <Fragment key={item.key}>
              <BodyItem>{item.title}</BodyItem>
              <BodyItem>{actItems[item.key]?.value}</BodyItem>
            </Fragment>
          ))}
        </Body>
      )}

      {section.footer && <Footer>{section.footer.map(getSection)}</Footer>}
    </FrontPageDiv>
  )
}
